import React, { useEffect, useRef, useState } from 'react';
import { TodoListMember } from '@dreamai/types';
import { ReactComponent as PersonIcon } from '../../../icons/person-circle.svg';
import MemberAvatar from './MemberAvatar';
import './assignee-picker.scss';

interface AssigneePickerProps {
  members: TodoListMember[];
  value: string | null;
  onChange: (userId: string | null) => void;
  currentUserId?: string;
  /** Visual variant: `action` matches AddTodoModal action buttons, `pill` is compact for list rows. */
  variant?: 'action' | 'pill';
  className?: string;
}

const MENU_MIN_WIDTH = 200;

export const AWAITING_JOIN_LABEL = 'Awaiting join';

/**
 * Dropdown to choose which list member a todo is assigned to. Accepted
 * members and pending invitees are offered; a pending invitee is shown as
 * "awaiting join" because the todo is hidden from them until they accept and
 * becomes theirs automatically at that point. Declined members are never
 * offered (the backend rejects them).
 */
const AssigneePicker: React.FC<AssigneePickerProps> = ({
  members,
  value,
  onChange,
  currentUserId,
  variant = 'action',
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Flip the menu to hang from the trigger's right edge when it would
  // otherwise run off the viewport (common on narrow screens).
  useEffect(() => {
    if (!open || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setAlignRight(rect.left + MENU_MIN_WIDTH > window.innerWidth - 12);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const accepted = members.filter((m) => m.status === 'accepted');
  const invited = members.filter((m) => m.status === 'pending');
  const assignable = [...accepted, ...invited];
  const selected = assignable.find((m) => m.userId === value) || null;
  const selectedAwaiting = selected?.status === 'pending';

  const labelFor = (m: TodoListMember) =>
    m.userId === currentUserId ? `${m.name} (you)` : m.name;

  const renderOption = (m: TodoListMember) => (
    <button
      type='button'
      key={m.userId}
      role='option'
      aria-selected={m.userId === value}
      className={`assignee-picker-item${m.userId === value ? ' selected' : ''}${m.status === 'pending' ? ' awaiting' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onChange(m.userId === value ? null : m.userId);
        setOpen(false);
      }}
    >
      <MemberAvatar name={m.name} size={22} />
      <span className='assignee-picker-item-text'>
        <span>{labelFor(m)}</span>
        {m.status === 'pending' && (
          <span className='assignee-picker-item-sub'>
            Invited · assigned when they join
          </span>
        )}
      </span>
    </button>
  );

  return (
    <div
      className={`assignee-picker assignee-picker--${variant}${className ? ` ${className}` : ''}`}
      ref={wrapperRef}
    >
      <button
        type='button'
        className={`assignee-picker-trigger${open ? ' active' : ''}${selected ? ' has-assignee' : ''}${selectedAwaiting ? ' awaiting' : ''}`}
        title={
          selected && selectedAwaiting
            ? `${selected.name} hasn't joined yet — this task is hidden from them and becomes theirs when they accept`
            : undefined
        }
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-haspopup='listbox'
        aria-expanded={open}
      >
        {selected ? (
          <>
            <MemberAvatar name={selected.name} size={18} />
            <span className='assignee-picker-label'>{labelFor(selected)}</span>
            {selectedAwaiting && (
              <span
                className='assignee-picker-awaiting'
                data-testid='assignee-awaiting-join'
              >
                {AWAITING_JOIN_LABEL}
              </span>
            )}
          </>
        ) : (
          <>
            <PersonIcon className='assignee-picker-icon' />
            <span className='assignee-picker-label'>Assign</span>
          </>
        )}
      </button>
      {open && (
        <div
          className={`assignee-picker-menu${alignRight ? ' align-right' : ''}`}
          role='listbox'
        >
          {assignable.length === 0 && (
            <div className='assignee-picker-empty'>No one to assign yet</div>
          )}
          {accepted.map(renderOption)}
          {invited.length > 0 && (
            <div className='assignee-picker-group'>Invited · awaiting join</div>
          )}
          {invited.map(renderOption)}
          {selected && (
            <button
              type='button'
              className='assignee-picker-item clear'
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
                setOpen(false);
              }}
            >
              Unassign
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AssigneePicker;
